import React from 'react';
import PropTypes from 'prop-types';
import { convertToPercentString } from 'utilities';

/**
 * Converts an object containing raw drm values into an object of
 * percentage values that add up to 100.0% and are keyed by CSS class name.
 * These ratios represent the user's drm usage, which is displayed via bar graph.
 *
 * @param {Object} drm - raw drm values
 * @returns {Object} drm percentages that add up to 100.0%
 */
const getBarValues = (drm, error) => {
  const currentPercent = (100 / drm.availablePercent) * drm.currentPercent;
  const selectionPercent = error
    ? 100 - currentPercent
    : (100 / drm.availablePercent) * drm.newPercent;
  const currentAndSelectionPercent = currentPercent + selectionPercent;

  const values = {
    currentPercent,
    // only return selectionPercent if a selection has been made
    ...(drm.newPercent && { selectionPercent }),
    // only return remainingPercent if selection does not exceed DRM allowance
    ...(!error && {
      remainingPercent: 100 - currentAndSelectionPercent,
      remainingPercentX: currentAndSelectionPercent,
    }),
  };

  // format all entries so they can be used in CSS by the svg rect
  Object.entries(values).forEach(
    ([key, value]) => (values[key] = convertToPercentString(value / 100)),
  );

  return values;
};

const UsageBarGraph = ({ drm, id, ...props }) => {
  const error = drm.newPercent + drm.currentPercent > drm.availablePercent;
  const barValues = getBarValues(drm, error);

  const classes = {
    bar: {
      borderRadius: '0.5em',
      display: 'block',
      height: '12px',
      outlineOffset: '-1px',
      width: '100%',
    },
    outline: {
      outline: '1px solid #4d4d4d', // grey[700]
    },

    currentPercent: {
      fill: '#002242', // shelfBlue[800]
      height: '12px',
      width: barValues.currentPercent,
      x: '0%',
    },
    remainingPercent: {
      fill: '#DBEEFF',
      height: '12px',
      width: barValues?.remainingPercent,
      x: barValues?.remainingPercentX,
    },
    selectionPercent: {
      fill: error ? '#D32F2F' : '#0071DB',
      height: '12px',
      width: barValues?.selectionPercent,
      x: barValues.currentPercent,
    },
  };

  return (
    <svg
      id={`usage-bar-graph-${id}`}
      aria-hidden="true"
      aria-describedby={`usage-bar-legend-${id}`}
      style={{ ...classes.bar, ...classes.outline }}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect
        style={{ ...classes.currentPercent, ...classes.outline }}
        id={`current-percent-graph-${id}`}
      ></rect>
      {drm.newPercent ? (
        <rect
          style={{ ...classes.selectionPercent, ...classes.outline }}
          id={`selection-percent-graph-${id}`}
        ></rect>
      ) : null}
      {barValues?.remainingPercent ? (
        <rect
          style={{ ...classes.remainingPercent, ...classes.outline }}
          id={`remaining-percent-graph-${id}`}
        ></rect>
      ) : null}
    </svg>
  );
};

UsageBarGraph.propTypes = {
  drm: PropTypes.shape({
    /**
     * Percent of DRM available
     */
    availablePercent: PropTypes.number.isRequired,
    /**
     * Percent of DRM that has been used
     */
    currentPercent: PropTypes.number.isRequired,
    /**
     * Percent of DRM user is attempting to add
     */
    newPercent: PropTypes.number,
    /**
     * Percent of DRM that is remaining
     */
    remainingAllowance: PropTypes.number.isRequired,
    /**
     * Should the legend always display the new percent list item
     */
    shouldShowNewPercent: PropTypes.bool,
    /**
     * Menu-specific label for currentPercent
     */
    usageText: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * ID used to connect the rects to the corresponding legend list items
   */
  id: PropTypes.string.isRequired,
};

export default UsageBarGraph;
